<template>
  <div>
    <section class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1>Delivery Report</h1>
          </div>
        </div>
      </div>
    </section>
    <div class="loading" v-if="loading">
      <hour-glass></hour-glass>
    </div>
    <section class="content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-12">
            <div class="card card-success">
              <div class="card-header">
                <h3 class="card-title">Delivery Partner Transaction Report</h3>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group">
                      <input
                        type="text"
                        class="start-date form-control"
                        id="start-date"
                        placeholder="From Date"
                        onkeydown="return false"
                        autocomplete="off"
                      />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <input
                        type="text"
                        class="end-date form-control"
                        id="end-date"
                        placeholder="To Date"
                        onkeydown="return false"
                        autocomplete="off"
                      />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <multiselect
                        v-model="selectedStore"
                        placeholder="Select Store"
                        label="retailer"
                        track-by="merchant_id"
                        :options="storeList"
                        :multiple="false"
                        :searchable="true"
                        :show-labels="false"
                      ></multiselect>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card-footer">
                <button
                  type="button"
                  class="btn btn-success"
                  @click="generateReport(false)"
                  :disabled="loading"
                >
                  <i class="fas fa-search"></i> Generate
                </button>
                <button
                  type="button"
                  class="btn btn-danger ml-10"
                  @click="generateReport(true)"
                  :disabled="loading || reportData.length === 0"
                >
                  <i class="fas fa-file-excel"></i> Generate & Export
                </button>
              </div>

              <div v-if="reportGenerated">
                <div class="card-body" v-if="summary">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="info-box">
                        <span class="info-box-icon bg-info"><i class="fas fa-list"></i></span>
                        <div class="info-box-content">
                          <span class="info-box-text">Total Records</span>
                          <span class="info-box-number">{{ summary.total_records }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="info-box">
                        <span class="info-box-icon bg-success"><i class="fas fa-dollar-sign"></i></span>
                        <div class="info-box-content">
                          <span class="info-box-text">Total Order Amount</span>
                          <span class="info-box-number">${{ summary.total_order_amount }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="info-box">
                        <span class="info-box-icon bg-warning"><i class="fas fa-truck"></i></span>
                        <div class="info-box-content">
                          <span class="info-box-text">Total Delivery Fee</span>
                          <span class="info-box-number">${{ summary.total_delivery_fee }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-body" v-if="reportData.length > 0">
                  <b-table-simple responsive show-empty bordered sticky-header="800px">
                    <b-thead head-variant="light" style="position: sticky; top: 0;">
                      <b-tr>
                        <b-th class="text-center">Transaction No.</b-th>
                        <b-th class="text-center">Date</b-th>
                        <b-th class="text-center">Time</b-th>
                        <b-th class="text-center">Source Store</b-th>
                        <b-th class="text-center">Delivery Store</b-th>
                        <b-th class="text-center">Terminal</b-th>
                        <b-th class="text-center">Order Amount</b-th>
                        <b-th class="text-center">Tip</b-th>
                        <b-th class="text-center">Delivery Fee</b-th>
                        <b-th class="text-center">Total Payment</b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody>
                      <b-tr v-for="(row, index) in reportData" :key="index">
                        <b-td class="text-left">{{ row.transaction_number }}</b-td>
                        <b-td class="text-left text-nowrap">{{ row.transaction_date }}</b-td>
                        <b-td class="text-left text-nowrap">{{ row.transaction_time }}</b-td>
                        <b-td class="text-left">{{ row.source_store_name }}</b-td>
                        <b-td class="text-left">{{ row.delivery_store_name }}</b-td>
                        <b-td class="text-left">{{ row.terminal_name }}</b-td>
                        <b-td class="text-right text-nowrap">${{ row.order_amount }}</b-td>
                        <b-td class="text-right text-nowrap">${{ row.tip_amount }}</b-td>
                        <b-td class="text-right text-nowrap">${{ row.delivery_fee }}</b-td>
                        <b-td class="text-right text-nowrap">${{ row.total_payment }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </div>

                <div class="card-body" v-if="reportData.length === 0">
                  <p class="text-center text-muted">No delivery transactions found for the selected date range.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { HourGlass } from "vue-loading-spinner";
import api from "@/api/reports.js";
import apiuser from "@/api/user.js";
import moment from "moment";

export default {
  components: {
    HourGlass,
  },
  data() {
    return {
      from_date: "",
      to_date: "",
      selectedStore: null,
      storeList: [],
      reportData: [],
      summary: null,
      reportGenerated: false,
      loading: false,
    };
  },
  mounted() {
    let self = this;
    const defaultFromDate = moment().subtract(7, "days");
    const defaultToDate = moment();

    $("#start-date").datepicker({
      format: "mm/dd/yyyy",
      autoclose: true,
      todayHighlight: true,
    }).on("changeDate", function (e) {
      const selectedStart = moment(e.date);
      const maxEndDate = selectedStart.clone().add(1, "month");

      self.from_date = selectedStart.format("MM/DD/YYYY");

      const currentEnd = moment(self.to_date || $("#end-date").val(), "MM/DD/YYYY", true);
      if (!currentEnd.isValid() || currentEnd.isBefore(selectedStart)) {
        self.to_date = selectedStart.format("MM/DD/YYYY");
        $("#end-date").datepicker("setDate", selectedStart.toDate());
      } else if (currentEnd.diff(selectedStart, "months", true) > 1) {
        self.to_date = maxEndDate.format("MM/DD/YYYY");
        $("#end-date").datepicker("setDate", maxEndDate.toDate());
      }
    });
    $("#end-date").datepicker({
      format: "mm/dd/yyyy",
      autoclose: true,
      todayHighlight: true,
    }).on("changeDate", function (e) {
      const selectedEnd = moment(e.date);

      self.to_date = selectedEnd.format("MM/DD/YYYY");
      const currentStart = moment(self.from_date || $("#start-date").val(), "MM/DD/YYYY", true);

      if (currentStart.isValid()) {
        if (selectedEnd.isBefore(currentStart)) {
          self.from_date = selectedEnd.format("MM/DD/YYYY");
          $("#start-date").datepicker("setDate", selectedEnd.toDate());
        } else if (selectedEnd.diff(currentStart, "months", true) > 1) {
          const maxEndDate = currentStart.clone().add(1, "month");
          self.to_date = maxEndDate.format("MM/DD/YYYY");
          $("#end-date").datepicker("setDate", maxEndDate.toDate());
        }
      }
    });
    $("#start-date").datepicker("setDate", defaultFromDate.toDate());
    $("#end-date").datepicker("setDate", defaultToDate.toDate());
    self.from_date = defaultFromDate.format("MM/DD/YYYY");
    self.to_date = defaultToDate.format("MM/DD/YYYY");
    self.getAuthorizedStores();
    document.title = "CanPay - Delivery Report";
  },
  methods: {
    getAuthorizedStores() {
      var self = this;
      apiuser
        .getStores()
        .then(function (response) {
          if (response.code == 200) {
            self.storeList = response.data;
          } else {
            error(response.message);
          }
        })
        .catch(function (err) {
          console.error(err);
        });
    },
    generateReport(reportExport) {
      let self = this;

      let fromDate = moment(self.from_date || $("#start-date").val(), "MM/DD/YYYY", true);
      let toDate = moment(self.to_date || $("#end-date").val(), "MM/DD/YYYY", true);

      if (!fromDate.isValid()) {
        error("Please select From Date.");
        return;
      }
      if (!toDate.isValid()) {
        error("Please select To Date.");
        return;
      }

      if (toDate.isBefore(fromDate)) {
        error("To Date cannot be earlier than From Date.");
        return;
      }

      if (toDate.diff(fromDate, "months", true) > 1) {
        error("Date range cannot be greater than one month.");
        return;
      }

      self.loading = true;
      let request = {
        from_date: fromDate.format("YYYY-MM-DD"),
        to_date: toDate.format("YYYY-MM-DD"),
      };

      if (self.selectedStore) {
        request.merchant_id = self.selectedStore.merchant_id;
      }

      if (reportExport) {
        request.export = true;
        api
          .generateDeliveryReport(request)
          .then((response) => {
            self.loading = false;
            let blob = new Blob([response], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            let link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download =
              "delivery_report_" +
              moment().format("MM/DD/YYYY") +
              ".xlsx";
            link.click();
          })
          .catch((err) => {
            self.loading = false;
            console.error(err);
          });
      } else {
        api
          .generateDeliveryReport(request)
          .then((response) => {
            self.loading = false;
            if (response.code == 200 && response.data) {
              self.reportData = response.data.data || [];
              self.summary = response.data.summary || null;
              self.reportGenerated = true;
            } else {
              self.reportData = [];
              self.summary = null;
              self.reportGenerated = true;
            }
          })
          .catch((err) => {
            self.loading = false;
            console.error(err);
          });
      }
    },
  },
};
</script>
